import 'dayjs/plugin/isBetween';
import 'dayjs/plugin/timezone';
import dayjs from 'dayjs';
import humps from 'humps';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';

import { useCommunityVisionAi } from '@/app/community/composables/useCommunityVisionAi';
import { useDashboardsVisionAi } from '@/app/dashboards/composables/useDashboardsVisionAi';
import { useListeningVisionAi } from '@/app/socialListening/composables/useListeningVisionAi';
import { INTENT_TYPES, SKIP_CAMELIZE_CONTENT_INTENT_TYPES } from '@/components/VisionAi/constants';
import { VISION_AI_SENDER } from '@/config';
import { convertDateToUtcDateTimeByZoneISOString } from '@/utils';
import { getUserTimezone } from '@/utils/timezone';
import { day } from '@/utils/vision';

interface IntentSourceLinkParams {
  brandLabel?: string;
  id?: string | number;
  platform?: string;
  topicId?: string | number;
  [key: string]: unknown;
}

interface IntentQuery {
  sourceLinkParams?: IntentSourceLinkParams;
  dashboardId?: string | number;
  [key: string]: unknown;
}

type IntentUrlConstructor = (intentQuery?: IntentQuery) => unknown;

interface VisionAiMessage {
  sender?: string;
  intentType?: string;
  intentQuery?: Record<string, unknown> | null;
  intent_type?: string;
  additional_content?: unknown;
  sourceUrl?: unknown;
}

interface Conversation {
  createdAt: string;
  [key: string]: unknown;
}

interface ConversationGroup {
  date: string;
  conversations: Conversation[];
}

const MESSAGE_DAYS_AGO = 30;

const EMPTY_SOURCE_LINK_PARAMS: IntentSourceLinkParams = {};

export const INTENT_ROUTE_MAPPING: Record<string, IntentUrlConstructor> = {
  [INTENT_TYPES.SAMPLE]: (intentQuery) => {
    const baseUrl = '/library/discover';

    if (!intentQuery) return baseUrl;
    // Construct query string from intentQuery object
    const queryParams = new URLSearchParams(
      humps.decamelizeKeys(intentQuery) as Record<string, string>,
    );
    return queryParams ? `${baseUrl}?${queryParams.toString()}` : baseUrl;
  },
  [INTENT_TYPES.DMS]: (intentQuery) => {
    const baseUrl = '/community/all-messages';
    const { getDMUrlFromIntentQuery } = useCommunityVisionAi();
    const { brandLabel, id } = intentQuery?.sourceLinkParams || EMPTY_SOURCE_LINK_PARAMS;

    return brandLabel && id ? getDMUrlFromIntentQuery(intentQuery) : baseUrl;
  },
  [INTENT_TYPES.COMMENTS]: (intentQuery) => {
    const baseUrl = '/community/all-messages';
    const { getCommentUrlFromIntentQuery } = useCommunityVisionAi();
    const { brandLabel, id, platform } = intentQuery?.sourceLinkParams || EMPTY_SOURCE_LINK_PARAMS;

    return brandLabel && id && platform ? getCommentUrlFromIntentQuery(intentQuery) : baseUrl;
  },
  [INTENT_TYPES.TOPICS]: (intentQuery) => {
    const baseUrl = '/listening/topics/';
    const { getTopicUrlFromIntentQuery } = useListeningVisionAi();
    const { topicId, brandLabel } = intentQuery?.sourceLinkParams || EMPTY_SOURCE_LINK_PARAMS;
    return topicId && brandLabel ? getTopicUrlFromIntentQuery(intentQuery) : baseUrl;
  },
  [INTENT_TYPES.TRENDS]: (intentQuery) => {
    const baseUrl = '/listening/topics/';
    const { getTrendsUrlFromIntentQuery } = useListeningVisionAi();
    const { brandLabel } = intentQuery?.sourceLinkParams || EMPTY_SOURCE_LINK_PARAMS;
    return brandLabel ? getTrendsUrlFromIntentQuery(intentQuery) : baseUrl;
  },
  [INTENT_TYPES.DASHBOARD_REPORTS]: (intentQuery) => {
    const baseUrl = '/dashboards';

    const { getDashboardUrlFromIntentQuery } = useDashboardsVisionAi();
    return intentQuery?.dashboardId ? getDashboardUrlFromIntentQuery(intentQuery) : baseUrl;
  },
  // Add more intent types and their custom URL construction functions as needed
};

export function enrichMessages(messages: VisionAiMessage[]): VisionAiMessage[] {
  return messages
    .map((message) => {
      if (message.sender === VISION_AI_SENDER.USER) return message;
      const sourceUrlConstructor = message.intentType
        ? INTENT_ROUTE_MAPPING[message.intentType]
        : undefined;
      return {
        ...message,
        sourceUrl: sourceUrlConstructor
          ? sourceUrlConstructor(message.intentQuery as IntentQuery | undefined)
          : null,
      };
    })
    .reverse();
}

/**
 * Camelizes message keys while optionally preserving `additional_content` in its original casing.
 * The backend returns additional_content with key formats (e.g. metric names, report field names)
 * that don't survive camelization cleanly. For METRIC_ANALYTICS, UNIFIED and DASHBOARD_REPORTS intent types,
 * we skip camelizing additional_content so existing dashboard report components can consume the
 * data directly without casing mismatches.
 */
export function skipCamelizeMessageKeysOnDashboardReportsAdditionalContent(
  messages: VisionAiMessage[] | null | undefined,
): VisionAiMessage[] {
  if (!messages) return [];

  return messages.map((message) => {
    const intentType = message.intent_type;
    if (intentType === undefined || !SKIP_CAMELIZE_CONTENT_INTENT_TYPES.includes(intentType)) {
      return humps.camelizeKeys(message) as VisionAiMessage;
    }

    const additionalContent = message.additional_content;
    delete message.additional_content;
    return { ...humps.camelizeKeys(message), additionalContent } as VisionAiMessage;
  });
}

export function processMessages(messages: VisionAiMessage[]): VisionAiMessage[] {
  // combines the above utils into one convenient method
  const casedMessages = skipCamelizeMessageKeysOnDashboardReportsAdditionalContent(messages);
  const enrichedMessages = enrichMessages(casedMessages);
  return enrichedMessages;
}

export function getStartTimeForApiRequest(startTime?: string | Date | null): string | Date {
  if (startTime) {
    return startTime;
  }

  const timezone = getUserTimezone();
  const startDate = dayjs().tz(timezone).subtract(MESSAGE_DAYS_AGO, 'days');
  return convertDateToUtcDateTimeByZoneISOString(startDate, timezone);
}

export function getEndTimeForApiRequest(endTime?: string | Date | null): string | Date {
  if (endTime) {
    return endTime;
  }

  const timezone = getUserTimezone();
  const endDate = dayjs().tz(timezone);
  return convertDateToUtcDateTimeByZoneISOString(endDate, timezone);
}

export function formatConversationDate(date: string): string {
  const formatType = 'YYYY-MM-DD';
  const currDate = dayjs().format(formatType);
  const yesterdaysDate = dayjs().subtract(1, 'day').format(formatType);
  const lastSevenDaysDate = dayjs().subtract(7, 'day').format(formatType);
  const last30DaysDate = dayjs().subtract(30, 'day').format(formatType);
  const parsedDate = dayjs(new Date(date));
  const formattedDate = parsedDate.format(formatType);

  if (formattedDate === currDate) return day.today;
  if (formattedDate === yesterdaysDate) return day.yesterday;
  if (dayjs(formattedDate).isBetween(lastSevenDaysDate, yesterdaysDate, 'day', '[)'))
    return day.lastSevenDays;
  if (dayjs(formattedDate).isBetween(last30DaysDate, lastSevenDaysDate, 'day', '[)'))
    return day.last30Days;

  return parsedDate.format('MMMM YYYY');
}

export function groupConversationsByDate(visionAiData: Conversation[]): ConversationGroup[] {
  const conversationsByDate = visionAiData.map((item) => {
    const date = formatConversationDate(item.createdAt);
    return {
      ...item,
      date,
    };
  });

  const GroupedData = Object.entries(groupBy(conversationsByDate, 'date')).map(
    ([date, conversations]) => ({
      date,
      conversations: conversations.reverse(),
    }),
  );

  return GroupedData;
}

export function orderByDate(
  visionAiData: ConversationGroup[],
  groupedByName: keyof ConversationGroup,
  order: Array<'asc' | 'desc'> = ['asc'],
): ConversationGroup[] {
  return orderBy(
    visionAiData,
    [(group) => new Date((group[groupedByName] as Conversation[])[0].createdAt)],
    order,
  );
}
